import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import { verifyPayment } from '../api/stripe';
import { useSettings } from '../context/SettingsContext';

const BookingSuccess = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { settings } = useSettings();
    const waPhone = (settings.company_whatsapp || "+212771373922").replace(/[^0-9]/g, "");
    const currencySymbol = settings.currency_symbol || "$";
    const [loading, setLoading] = useState(true);
    const [booking, setBooking] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Check if booking data was passed via navigation state (PayPal)
        if (location.state?.booking) {
            setBooking(location.state.booking);
            setLoading(false);
            return;
        }

        // Otherwise verify via Stripe session
        const verifyBookingPayment = async () => {
            const sessionId = searchParams.get('session_id');
            const bookingId = searchParams.get('booking_id');

            if (!sessionId || !bookingId) {
                setError('Missing payment information');
                setLoading(false);
                return;
            }

            try {
                const response = await verifyPayment(sessionId, bookingId);
                if (response.success) {
                    setBooking(response.booking);
                } else {
                    setError(response.message || 'Verification error');
                }
            } catch (err) {
                setError('Error verifying payment');
            } finally {
                setLoading(false);
            }
        };

        verifyBookingPayment();
    }, [searchParams, location.state]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-emerald-600 mx-auto mb-4"></div>
                    <p className="text-lg text-slate-600 dark:text-slate-300">Verifying payment...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
                <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 text-center border border-slate-200 dark:border-slate-700">
                    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Error</h1>
                    <p className="text-slate-600 dark:text-slate-300 mb-6">{error}</p>
                    <Link
                        to="/"
                        className="inline-block bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition"
                    >
                        Back to Home
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4 py-12">
            <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 text-center border border-slate-200 dark:border-slate-700">
                <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Payment Successful!</h1>
                <p className="text-slate-600 dark:text-slate-300 mb-6">
                    Thank you for your booking. A confirmation email has been sent to you.
                </p>

                {booking && (
                    <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5 mb-6 text-left border border-slate-200 dark:border-slate-600">
                        <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                            <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Booking Details
                        </h3>
                        <div className="space-y-2">
                            <p className="text-sm text-slate-600 dark:text-slate-300 flex justify-between">
                                <span className="font-medium">Tour:</span>
                                <span className="text-right">{booking.tour?.title}</span>
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-300 flex justify-between">
                                <span className="font-medium">Date:</span>
                                <span>{new Date(booking.booking_date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</span>
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-300 flex justify-between">
                                <span className="font-medium">Travelers:</span>
                                <span>{booking.number_of_people} {booking.number_of_people > 1 ? 'people' : 'person'}</span>
                            </p>
                            <div className="border-t border-slate-200 dark:border-slate-600 pt-2 mt-2 space-y-1">
                                <p className="text-sm text-slate-600 dark:text-slate-300 flex justify-between">
                                    <span>Total Price:</span>
                                    <span>{currencySymbol}{booking.total_price}</span>
                                </p>
                                <p className="text-sm text-slate-600 dark:text-slate-300 flex justify-between font-semibold">
                                    <span>Deposit Paid:</span>
                                    <span className="text-emerald-600 dark:text-emerald-400">{currencySymbol}{booking.amount_paid || booking.deposit_amount || booking.total_price}</span>
                                </p>
                                {booking.remaining_amount > 0 && (
                                    <p className="text-sm text-slate-600 dark:text-slate-300 flex justify-between">
                                        <span>Remaining (pay on tour day):</span>
                                        <span className="text-amber-600 dark:text-amber-400">{currencySymbol}{booking.remaining_amount}</span>
                                    </p>
                                )}
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-300 flex justify-between items-center">
                                <span className="font-medium">Status:</span>
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    Confirmed
                                </span>
                            </p>
                        </div>
                    </div>
                )}

                {/* Contact Info */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6 text-left border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-800 dark:text-blue-300 font-medium mb-2">Questions? Contact us directly</p>
                    <p className="text-xs text-blue-700 dark:text-blue-400">
                        WhatsApp: <a href={`https://wa.me/${waPhone}`} className="underline hover:no-underline">{settings.company_whatsapp}</a> | Email: <a href={`mailto:${settings.company_email}`} className="underline hover:no-underline">{settings.company_email}</a>
                    </p>
                </div>

                <div className="space-y-3">
                    <Link
                        to="/"
                        className="block w-full bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition font-semibold"
                    >
                        Back to Home
                    </Link>
                    <Link
                        to="/tours"
                        className="block w-full border-2 border-emerald-600 text-emerald-600 dark:text-emerald-400 px-6 py-3 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition font-semibold"
                    >
                        Browse More Tours
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default BookingSuccess;
